import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@libero/core/services/api';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';

// ── Typen (spiegeln backend/src/aggregator.ts + service.ts) ────────────────
type Quelle = 'kunde' | 'manuell' | 'unbekannt';
interface QuelleStat {
  erstellt: number;
  angenommen: number;
  abgelehnt: number;
  offen: number;
  storniert: number;
  ungeklaert: number;
  auftragStorniert: number;
  angebotenesEuro: number;
  angenommenEuro: number;
  abgelehntEuro: number;
  offenEuro: number;
  storniertEuro: number;
  ungeklaertEuro: number;
  auftragStorniertEuro: number;
  /** KPI-02: Summe der aktiven Auftragswerte der gewonnenen Angebote. */
  realisiertEuro: number;
  /** REV-02: gewonnen, aber kein Auftrag auffindbar (Fallback Angebotswert). */
  gewonnenOhneAuftrag: number;
  gewonnenOhneAuftragEuro: number;
  /** angenommen / (angenommen + abgelehnt) — NUR entschiedene Angebote, null wenn Nenner 0. */
  conversion: number | null;
}
type Ausschlussgrund = 'testkontakt' | 'kein-angebot' | 'alt-status';
interface AusschlussStat {
  anzahl: number;
  euro: number;
  gruende: Record<Ausschlussgrund, number>;
}
interface Analyse {
  jeQuelle: Record<Quelle, QuelleStat>;
  gesamt: QuelleStat;
  ausgeschlossen: AusschlussStat;
}

const AUSSCHLUSS_LABEL: Record<Ausschlussgrund, string> = {
  testkontakt: 'Test-Kontakte',
  'kein-angebot': 'nie ein Angebot gewesen',
  'alt-status': 'Alt-Status 3.1',
};
interface UngeklaertHinweis {
  orderId: number;
  statusId: string | null;
  createdAt: string;
  invoiceTotal: number;
}
interface StatusAbweichung {
  orderId: number;
  statusId: string | null;
  createdAt: string;
  invoiceTotal: number;
  realisiertEuro: number;
  auftragIds: number[];
}
interface PlentyAbgleich {
  plentyStatus332: number;
  ausgeschlossen: number;
  auftragStorniert: number;
  ueberAuftragGewonnen: number;
  angenommen: number;
}
interface GroessenklasseStat {
  label: string;
  vonEur: number;
  bisEur: number | null;
  stat: QuelleStat;
}
interface DauerKennzahlen {
  faelle: number;
  medianTage: number | null;
  schnittTage: number | null;
  p90Tage: number | null;
}
interface DauerKlasse {
  label: string;
  anzahl: number;
  anteil: number;
  kumuliert: number;
}
interface AnnahmedauerAnalyse {
  gesamt: DauerKennzahlen;
  klassen: DauerKlasse[];
  jeGroesse: (DauerKennzahlen & { label: string })[];
  jeQuelle: (DauerKennzahlen & { label: string })[];
}
interface StatsResult {
  stats: Analyse;
  from: string | null;
  to: string | null;
  lastRunAt: string | null;
  totalRows: number;
  ungeklaertListe: UngeklaertHinweis[];
  statusAbweichungen: StatusAbweichung[];
  abgleich: PlentyAbgleich;
  groessenklassen: GroessenklasseStat[];
  annahmedauer: AnnahmedauerAnalyse;
}
interface StatusResult {
  lastRunAt: string | null;
  totalOrders: number;
  offen: number;
  months: number;
}

function euro(n: number): string {
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}
function num(n: number): string {
  return n.toLocaleString('de-DE');
}
function pctFmt(n: number): string {
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}
function formatDateTime(iso: string | null): string {
  if (!iso) return 'noch nie';
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}
function tage(n: number | null): string {
  if (n === null) return '—';
  if (n < 1) return n < 0.05 ? 'am selben Tag' : `${n.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Tage`;
  return `${n.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Tage`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Horizontale Balken-Reihe (anzahl-skaliert): Anzahl, angebotenes € und Anteil. */
function BarRow({
  label, color, anzahl, eur, max, total,
}: { label: string; color: string; anzahl: number; eur: number; max: number; total: number }) {
  const barPct = max > 0 && anzahl > 0 ? Math.max(2, Math.round((anzahl / max) * 100)) : 0;
  const share = total > 0 ? (anzahl / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="w-56 shrink-0 text-sm text-[#1A1A1A]">{label}</div>
      <div className="flex-1 bg-[#F3F3F1] rounded h-6 relative overflow-hidden">
        <div className="h-6 rounded" style={{ width: `${barPct}%`, backgroundColor: color }} />
      </div>
      <div className="w-16 shrink-0 text-right text-sm font-semibold text-[#1A1A1A]">{num(anzahl)}</div>
      <div className="w-32 shrink-0 text-right text-sm text-[#6B6B63]">{euro(eur)}</div>
      <div className="w-16 shrink-0 text-right text-sm text-[#6B6B63]">{pctFmt(share)}</div>
    </div>
  );
}

/** Balken auf €-Skala (für den Vergleich angeboten vs. realisiert). */
function EuroBarRow({ label, color, eur, max }: { label: string; color: string; eur: number; max: number }) {
  const barPct = max > 0 && eur > 0 ? Math.max(2, Math.round((eur / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="w-56 shrink-0 text-sm text-[#1A1A1A]">{label}</div>
      <div className="flex-1 bg-[#F3F3F1] rounded h-6 relative overflow-hidden">
        <div className="h-6 rounded" style={{ width: `${barPct}%`, backgroundColor: color }} />
      </div>
      <div className="w-36 shrink-0 text-right text-sm font-semibold text-[#1A1A1A]">{euro(eur)}</div>
    </div>
  );
}

/**
 * Conversion einer Quelle als Karte — mit offengelegter Rechnung, damit die Rate
 * ohne Nachfragen nachvollziehbar ist (Wunsch Maxi 28.07.2026: „besser erklären
 * wie die Zahlen entstehen").
 */
function ConversionBlock({ label, hinweis, color, stat }: { label: string; hinweis: string; color: string; stat: QuelleStat }) {
  const entschieden = stat.angenommen + stat.abgelehnt + stat.auftragStorniert;
  const pct = stat.conversion === null ? null : stat.conversion * 100;
  return (
    <div className="border border-[#DAD9D9] rounded p-3 bg-white flex-1 min-w-[300px]">
      <div className="flex items-baseline gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-sm font-medium text-[#1A1A1A]">{label}</span>
        <span className="ml-auto text-2xl font-bold text-[#1A1A1A]">{pct === null ? '—' : pctFmt(pct)}</span>
      </div>
      <div className="text-xs text-[#9A9A90] ml-[18px]">{hinweis}</div>

      <div className="mt-2 h-5 bg-[#F3F3F1] rounded overflow-hidden">
        <div className="h-5 rounded" style={{ width: `${pct === null ? 0 : Math.max(2, Math.round(pct))}%`, backgroundColor: color }} />
      </div>

      {/* Die Rechnung selbst */}
      <div className="mt-3 text-sm text-[#1A1A1A]">
        <span className="font-semibold text-[#15803D]">{num(stat.angenommen)}</span> angenommen
        {' ÷ '}
        <span className="font-semibold">{num(entschieden)}</span> entschieden
        {' = '}
        <span className="font-semibold">{pct === null ? '—' : pctFmt(pct)}</span>
      </div>
      <table className="mt-2 w-full text-xs text-[#3A3A38]">
        <tbody>
          <tr>
            <td className="py-0.5">Angenommen (Auftrag entstanden bzw. Status 3.32)</td>
            <td className="text-right font-semibold text-[#15803D]">{num(stat.angenommen)}</td>
          </tr>
          <tr>
            <td className="py-0.5">+ Abgelehnt (3.34 oder &gt; 50 Tage ohne Entscheidung)</td>
            <td className="text-right text-[#DC2626]">{num(stat.abgelehnt)}</td>
          </tr>
          <tr>
            <td className="py-0.5">+ Auftrag storniert (zählt als abgelehnt)</td>
            <td className="text-right text-[#A16207]">{num(stat.auftragStorniert)}</td>
          </tr>
          <tr className="border-t border-[#DAD9D9]">
            <td className="py-0.5 font-semibold">= entschieden (Nenner der Rate)</td>
            <td className="text-right font-semibold">{num(entschieden)}</td>
          </tr>
          <tr>
            <td className="pt-2 text-[#6B6B63]">Nicht in der Rechnung: offen{stat.ungeklaert > 0 ? ' + ungeklärt' : ''}</td>
            <td className="pt-2 text-right text-[#6B6B63]">{num(stat.offen + stat.ungeklaert)}</td>
          </tr>
          <tr>
            <td className="text-[#6B6B63]">Angebote insgesamt</td>
            <td className="text-right text-[#6B6B63]">{num(stat.erstellt)}</td>
          </tr>
        </tbody>
      </table>
      <div className="mt-2 pt-2 border-t border-[#DAD9D9] text-xs text-[#6B6B63]">
        Angeboten <span className="font-medium text-[#1A1A1A]">{euro(stat.angebotenesEuro)}</span>
        {' · '}realisiert <span className="font-medium text-[#15803D]">{euro(stat.realisiertEuro)}</span>
      </div>
    </div>
  );
}

export default function App() {
  const [stats, setStats] = useState<StatsResult | null>(null);
  const [status, setStatus] = useState<StatusResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Die Auswertung startet bei 2026 (config.FIRST_DATE) — Datumsfilter deshalb
  // standardmäßig ab 01.01.2026, "bis" offen (= bis heute).
  const [from, setFrom] = useState('2026-01-01');
  const [to, setTo] = useState('');

  const runJob = useJobStatus(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const resp = await api.get<{ success: boolean; data: StatsResult }>(
        '/programmes/analyse-angebots-conversion/stats',
        { params },
      );
      if (resp.data.success) setStats(resp.data.data);
      else setError('Unerwartetes Antwortformat vom Server');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Statistik konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  const loadStatus = useCallback(async () => {
    try {
      const resp = await api.get<{ success: boolean; data: StatusResult }>(
        '/programmes/analyse-angebots-conversion/status',
      );
      if (resp.data.success) setStatus(resp.data.data);
    } catch {
      /* Status ist nebensächlich */
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (runJob.status === 'completed') {
      loadStats();
      loadStatus();
    }
  }, [runJob.status, loadStats, loadStatus]);

  const startRun = async (backfill: boolean) => {
    try {
      setError(null);
      await runJob.startJob('/programmes/analyse-angebots-conversion/run', { backfill });
    } catch (err: any) {
      const code = err.response?.data?.error?.code;
      setError(code === 'JOB_ALREADY_RUNNING'
        ? 'Ein Lauf läuft bereits — bitte warten.'
        : err.response?.data?.error?.message || 'Lauf konnte nicht gestartet werden');
    }
  };

  const s = stats?.stats;
  const g = s?.gesamt;

  // Balken-Skala Chart 1 = größter Ausgang (nicht "erstellt", sonst sind alle Balken winzig).
  const chart1Max = useMemo(() => {
    if (!g) return 0;
    return Math.max(1, g.angenommen, g.abgelehnt, g.offen, g.storniert, g.ungeklaert, g.auftragStorniert);
  }, [g]);
  // Entschieden = angenommen + abgelehnt + „Auftrag storniert" (letzteres zählt
  // wie abgelehnt, Entscheidung 28.07.2026). Offen/storniert/ungeklärt nicht.
  const entschieden = g ? g.angenommen + g.abgelehnt + g.auftragStorniert : 0;
  // €-Skala für Chart 3: der größere der beiden Werte bestimmt die Balkenlänge.
  const euroMax = useMemo(() => (g ? Math.max(1, g.angebotenesEuro, g.realisiertEuro) : 0), [g]);

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-2xl font-bold text-[#1A1A1A]">Analyse Angebots-Conversion</h1>
      <p className="text-sm text-[#6B6B63] mt-1">
        Ausgang der Angebote (orderType=7) und Conversion je Quelle (Kunde/manuell). Read-only.
      </p>

      {/* Erklär-Block: Klassifizierung + Quelle + Conversion-Basis */}
      <details className="mt-4 border border-[#DAD9D9] rounded bg-[#F9F9F7]">
        <summary className="cursor-pointer select-none px-4 py-2 text-sm font-semibold text-[#1A1A1A]">
          So funktioniert die Klassifizierung
        </summary>
        <div className="px-4 pb-4 pt-1 text-sm text-[#3A3A38] space-y-2">
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Angenommen (gewonnen):</strong> Status 3.32 — ODER es existiert ein aktiver
              Auftrag zum Angebot. Der Auftrag ist maßgeblich: der Angebotsstatus wird von Hand
              gepflegt und hinkt gelegentlich nach (siehe Liste unten).
            </li>
            <li>
              <strong>Auftrag storniert:</strong> Angebot war angenommen, aber alle daraus
              entstandenen Aufträge sind storniert → kein Geschäft. Zählt in der Conversion wie
              <strong> abgelehnt</strong> (eigene Zeile, damit die Fälle sichtbar bleiben),
              realisiert € = 0. Typische Verläufe: Auftrag auf Vorkasse umgestellt und nach 20 Tagen
              ohne Zahlung automatisch storniert (Status 8), oder Storno durch den Kunden (8.1).
            </li>
            <li><strong>Abgelehnt (verloren):</strong> Status 3.34, ODER seit Anlage mehr als 50 Tage vergangen und noch nicht entschieden.</li>
            <li><strong>Offen:</strong> noch nicht entschieden (Status 3.31 / 3.311 / 3.33) UND höchstens 50 Tage seit Anlage.</li>
            <li><strong>Storniert:</strong> Status 8. Eigener Ausgang — zählt bei „erstellt" mit, aber weder als gewonnen noch als verloren, und geht NICHT in die Conversion ein.</li>
            <li><strong>Ungeklärt:</strong> ein Status, den es an einem Angebot nicht geben sollte (z.B. <em>[3] Warten auf Zahlung</em>) — ein Pflegefehler in Plenty. Wird separat ausgewiesen statt still als „offen" mitzulaufen.</li>
            <li><strong>Quelle:</strong> über das Tag am Angebot (NICHT über den Status) — Tag 336 = Kunde, Tag 337 = manuell, kein Tag = unbekannt.</li>
            <li>
              <strong>Ausgeschlossen</strong> (zählt nirgends mit, wird unter Chart 1 separat ausgewiesen):
              Angebote der Test-Kontakte <em>Max Ebell</em> und <em>Kamil Czaja</em>; Datensätze die nie
              ein Angebot waren (kein Kunde UND kein Angebotsstatus); Legacy-Status <em>[3.1] Angebot ALT</em>.
              Ein Angebot OHNE Kunden, aber mit Angebotsstatus, zählt normal mit.
            </li>
            <li><strong>Prozentspalte in Chart 1:</strong> im Block „Entschieden" bezogen auf angenommen + abgelehnt (dieselbe Basis wie die Conversion), im unteren Block bezogen auf alle Angebote des Zeitraums.</li>
            <li><strong>Conversion-Rate:</strong> angenommen / (angenommen + abgelehnt) — bezieht sich NUR auf bereits entschiedene Angebote; offen, storniert und ungeklärt zählen nicht in den Nenner.</li>
          </ul>
          <p className="text-xs text-[#6B6B63]">
            Der Datumsfilter oben bezieht sich auf das <strong>Angebotsdatum</strong> (Anlage) und betrifft nur
            die Anzeige — die 50-Tage-Frist läuft davon unabhängig.
          </p>
        </div>
      </details>

      {/* Steuerung */}
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-sm text-[#1A1A1A]">
          Von (Angebotsdatum)
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="block mt-1 border border-[#DAD9D9] rounded px-2 py-1 text-sm" />
        </label>
        <label className="text-sm text-[#1A1A1A]">
          Bis
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="block mt-1 border border-[#DAD9D9] rounded px-2 py-1 text-sm" />
        </label>
        <button onClick={loadStats} disabled={loading}
          className="px-3 py-1.5 rounded border border-[#DAD9D9] text-sm text-[#1A1A1A] hover:bg-[#F3F3F1] disabled:opacity-50">
          Filter anwenden
        </button>
        <div className="flex-1" />
        <button onClick={() => startRun(false)} disabled={runJob.isRunning}
          className="px-3 py-1.5 rounded bg-[#E9FF57] text-[#1A1A1A] text-sm font-semibold hover:bg-[#d4ea4d] disabled:opacity-50">
          {runJob.isRunning ? 'Läuft…' : 'Jetzt aktualisieren'}
        </button>
        <button onClick={() => startRun(true)} disabled={runJob.isRunning}
          className="px-3 py-1.5 rounded border border-[#1A1A1A] text-sm text-[#1A1A1A] hover:bg-[#F3F3F1] disabled:opacity-50"
          title="Gesamten konfigurierten Zeitraum (ab 2026) neu einlesen">
          Backfill 2026
        </button>
      </div>

      {/* Status / Fortschritt */}
      <div className="mt-3 text-sm text-[#6B6B63]">
        Zuletzt aktualisiert: <span className="font-medium text-[#1A1A1A]">{formatDateTime(status?.lastRunAt ?? stats?.lastRunAt ?? null)}</span>
        {stats && (
          <>
            {' '}· <span title="Alle Datensätze mit orderType 7 im Zeitraum — entspricht dem Plenty-Filter „Auftragstyp: Angebot“">
              {num(stats.totalRows)} Datensätze im Zeitraum
            </span>
            {s && (
              <>
                {' '}· <span className="font-medium text-[#1A1A1A]" title="Rohmenge minus ausgeschlossene Datensätze — Basis aller Charts">
                  {num(s.gesamt.erstellt)} auswertbar
                </span>
              </>
            )}
          </>
        )}
      </div>
      {runJob.isRunning && runJob.progress && (
        <div className="mt-2">
          <div className="h-2 bg-[#DAD9D9] rounded">
            <div className="h-2 bg-[#E9FF57] rounded" style={{ width: `${runJob.progress.percentage ?? 0}%` }} />
          </div>
          <p className="text-xs text-[#6B6B63] mt-1">{runJob.progress.message}</p>
        </div>
      )}
      {error && <div className="mt-3 p-3 rounded bg-red-50 text-red-800 text-sm">{error}</div>}

      {!s && !loading && (
        <p className="mt-6 text-sm text-[#6B6B63]">Noch keine Daten — starte einen Lauf („Backfill 2026").</p>
      )}

      {s && g && g.erstellt === 0 && (
        <p className="mt-6 text-sm text-[#6B6B63]">Keine Angebote im gewählten Zeitraum.</p>
      )}

      {s && g && g.erstellt > 0 && (
        <>
          {/* Chart 1 — Ausgang aller Angebote im Zeitraum */}
          <section className="mt-6">
            <h2 className="text-lg font-semibold text-[#1A1A1A]">
              Chart 1 · Ausgang der Angebote
              <span className="ml-2 text-sm font-normal text-[#6B6B63]">
                ({num(g.erstellt)} Angebote · {euro(g.angebotenesEuro)})
              </span>
            </h2>
            <div className="mt-2 border border-[#DAD9D9] rounded p-3">
              {/* Entschiedene Angebote — Prozent auf der Conversion-Basis
                  (angenommen + abgelehnt), NICHT auf allen Angeboten: offene
                  Angebote gehören nicht in diese Quote. */}
              <div className="text-xs font-semibold uppercase text-[#6B6B63] mb-1">
                Entschieden — Basis der Conversion: {num(entschieden)} Angebote
              </div>
              <BarRow label="Angenommen (3.32)" color="#15803D"
                anzahl={g.angenommen} eur={g.angenommenEuro} max={chart1Max} total={entschieden} />
              <BarRow label="Abgelehnt (3.34 / &gt; 50 Tage)" color="#DC2626"
                anzahl={g.abgelehnt} eur={g.abgelehntEuro} max={chart1Max} total={entschieden} />
              {g.auftragStorniert > 0 && (
                <BarRow label="Auftrag storniert (zählt als abgelehnt)" color="#A16207"
                  anzahl={g.auftragStorniert} eur={g.auftragStorniertEuro} max={chart1Max} total={entschieden} />
              )}

              {/* Nicht entschieden bzw. außerhalb der Basis — hier ist der
                  Anteil an ALLEN Angeboten die sinnvolle Bezugsgröße. */}
              <div className="mt-3 pt-2 border-t border-[#DAD9D9] text-xs font-semibold uppercase text-[#6B6B63] mb-1">
                Nicht in der Conversion-Basis — Anteil an allen {num(g.erstellt)} Angeboten
              </div>
              <BarRow label="Offen (noch in der 50-Tage-Frist)" color="#B45309"
                anzahl={g.offen} eur={g.offenEuro} max={chart1Max} total={g.erstellt} />
              <BarRow label="Storniert (Status 8)" color="#78716C"
                anzahl={g.storniert} eur={g.storniertEuro} max={chart1Max} total={g.erstellt} />
              {g.ungeklaert > 0 && (
                <BarRow label="Ungeklärt (unerwarteter Status)" color="#9333EA"
                  anzahl={g.ungeklaert} eur={g.ungeklaertEuro} max={chart1Max} total={g.erstellt} />
              )}

              <div className="mt-2 pt-2 border-t border-[#DAD9D9] text-sm text-[#3A3A38]">
                <strong>Conversion gesamt: {g.conversion === null ? '—' : pctFmt(g.conversion * 100)}</strong>
                <span className="text-[#6B6B63]">
                  {' '}— {num(g.angenommen)} von {num(entschieden)} entschiedenen Angeboten.
                  „Auftrag storniert" zählt dabei wie abgelehnt (das Geschäft kam nicht zustande).
                  Offen, storniert (Angebot selbst) und ungeklärt bleiben außerhalb der Basis.
                </span>
              </div>

              {/* Abgleich mit dem Plenty-Statusfilter — erklärt die Differenz
                  zwischen „Status 3.32 in Plenty" und „Angenommen" hier. */}
              {stats && stats.abgleich.plentyStatus332 > 0 && (
                <details className="mt-2 pt-2 border-t border-[#DAD9D9]">
                  <summary className="cursor-pointer select-none text-xs font-semibold text-[#1A1A1A]">
                    Abgleich mit Plenty: Status 3.32 = {num(stats.abgleich.plentyStatus332)} → hier {num(stats.abgleich.angenommen)} angenommen
                  </summary>
                  <table className="mt-2 text-xs text-[#3A3A38]">
                    <tbody>
                      <tr>
                        <td className="pr-3 py-0.5">Plenty-Filter „Auftragstyp Angebot + Status [3.32]"</td>
                        <td className="text-right font-semibold text-[#1A1A1A]">{num(stats.abgleich.plentyStatus332)}</td>
                      </tr>
                      <tr>
                        <td className="pr-3 py-0.5">− Test-Kontakte / keine echten Angebote</td>
                        <td className="text-right">−{num(stats.abgleich.ausgeschlossen)}</td>
                      </tr>
                      <tr>
                        <td className="pr-3 py-0.5">− „Auftrag storniert" (Auftrag storniert → zählt als abgelehnt)</td>
                        <td className="text-right">−{num(stats.abgleich.auftragStorniert)}</td>
                      </tr>
                      <tr>
                        <td className="pr-3 py-0.5">+ gewonnen über aktiven Auftrag (Angebotsstatus ≠ 3.32)</td>
                        <td className="text-right">+{num(stats.abgleich.ueberAuftragGewonnen)}</td>
                      </tr>
                      <tr className="border-t border-[#DAD9D9]">
                        <td className="pr-3 py-0.5 font-semibold">= Angenommen in diesem Tool</td>
                        <td className="text-right font-semibold text-[#15803D]">{num(stats.abgleich.angenommen)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="mt-1 text-xs text-[#6B6B63]">
                    Die Plenty-Zahl ist die reine Statuszahl. Dieses Tool bewertet zusätzlich, ob aus dem
                    Angebot wirklich Geschäft wurde — deshalb die drei Umbuchungen. Beide Zahlen sind korrekt,
                    sie messen Unterschiedliches.
                  </p>
                </details>
              )}

              {/* Ausschluss-Zeile: was gar nicht als Angebot gezählt wurde */}
              {s.ausgeschlossen.anzahl > 0 && (
                <div className="mt-2 pt-2 border-t border-[#DAD9D9] text-xs text-[#6B6B63]">
                  Zusätzlich <strong>{num(s.ausgeschlossen.anzahl)} Datensätze ausgeschlossen</strong>
                  {' '}({euro(s.ausgeschlossen.euro)}) — nicht in den Zahlen oben enthalten:{' '}
                  {(Object.keys(AUSSCHLUSS_LABEL) as Ausschlussgrund[])
                    .filter((k) => (s.ausgeschlossen.gruende[k] ?? 0) > 0)
                    .map((k) => `${num(s.ausgeschlossen.gruende[k])} ${AUSSCHLUSS_LABEL[k]}`)
                    .join(' · ')}
                  . Test-Kontakte = Max Ebell / Kamil Czaja; „nie ein Angebot gewesen" = kein Kunde
                  UND kein Angebotsstatus (in Plenty direkt in einem Auftragsstatus angelegt);
                  Alt-Status = <em>[3.1] Angebot ALT</em>.
                </div>
              )}
            </div>
          </section>

          {/* Chart 2 — Conversion je Quelle */}
          <section className="mt-6">
            <h2 className="text-lg font-semibold text-[#1A1A1A]">
              Chart 2 · Conversion je Quelle
              <span className="ml-2 text-sm font-normal text-[#6B6B63]">
                (Quelle kommt aus dem Tag am Angebot, nicht aus dem Status)
              </span>
            </h2>
            <div className="mt-2 flex flex-wrap gap-3">
              <ConversionBlock label="Kunde (Tag 336)" hinweis="Kunde hat das Angebot selbst im Shop erzeugt"
                color="#2563EB" stat={s.jeQuelle.kunde} />
              <ConversionBlock label="Manuell (Tag 337)" hinweis="Angebot wurde intern angelegt"
                color="#0D9488" stat={s.jeQuelle.manuell} />
            </div>
            <p className="mt-2 text-xs text-[#6B6B63]">
              Beide Raten sind eigenständig und addieren sich nicht auf 100 % — jede bezieht sich nur
              auf die entschiedenen Angebote ihrer Quelle. Offene Angebote bleiben außen vor, weil noch
              nicht feststeht, wie sie ausgehen.
              {s.jeQuelle.unbekannt.erstellt > 0 && (
                <>
                  {' '}Nicht dargestellt: {num(s.jeQuelle.unbekannt.erstellt)} Angebote ohne Tag 336/337
                  {s.jeQuelle.unbekannt.ungeklaert > 0 && <> (davon {num(s.jeQuelle.unbekannt.ungeklaert)} Datenfehler in Plenty)</>}
                  {' '}— zu wenige für eine belastbare Quote, sie zählen aber in Chart 1 und Chart 3 mit.
                </>
              )}
            </p>
          </section>

          {/* Chart 3 — angeboten vs. realisiert (KPI-02) */}
          <section className="mt-6">
            <h2 className="text-lg font-semibold text-[#1A1A1A]">
              Chart 3 · Angeboten vs. realisiert
              <span className="ml-2 text-sm font-normal text-[#6B6B63]">(Brutto, Auftragswerte der gewonnenen Angebote)</span>
            </h2>
            <div className="mt-2 border border-[#DAD9D9] rounded p-3">
              <EuroBarRow label="Angeboten (alle Angebote)" color="#2563EB"
                eur={g.angebotenesEuro} max={euroMax} />
              <EuroBarRow label="Realisiert (aktive Aufträge)" color="#15803D"
                eur={g.realisiertEuro} max={euroMax} />
              <div className="mt-2 pt-2 border-t border-[#DAD9D9] text-xs text-[#6B6B63]">
                {g.angenommenEuro > 0 && (
                  <>
                    Realisierungsquote der gewonnenen Angebote:{' '}
                    <strong className="text-[#1A1A1A]">{pctFmt((g.realisiertEuro / g.angenommenEuro) * 100)}</strong>
                    {' '}({euro(g.realisiertEuro)} realisiert bei {euro(g.angenommenEuro)} angeboten).
                    <br />
                  </>
                )}
                Realisiert = Summe der Auftragswerte (invoiceTotal) aller aktiven Aufträge; stornierte
                Aufträge zählen 0 €. Bei mehreren Aufträgen zu einem Angebot werden die aktiven summiert.
                {g.gewonnenOhneAuftrag > 0 && (
                  <>
                    <br />
                    Davon <strong>{num(g.gewonnenOhneAuftrag)} gewonnene Angebote ohne auffindbaren Auftrag</strong>
                    {' '}({euro(g.gewonnenOhneAuftragEuro)} Angebotswert als Fallback) — bewusst NICHT in
                    „realisiert" enthalten.
                  </>
                )}
              </div>
            </div>
          </section>

          {/* Chart 4 — Conversion nach Angebotsgröße */}
          {stats && stats.groessenklassen.some((k) => k.stat.erstellt > 0) && (
            <section className="mt-6">
              <h2 className="text-lg font-semibold text-[#1A1A1A]">
                Chart 4 · Conversion nach Angebotsgröße
                <span className="ml-2 text-sm font-normal text-[#6B6B63]">(Klassen nach Angebotswert)</span>
              </h2>
              <div className="mt-2 border border-[#DAD9D9] rounded p-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-[#9A9A90]">
                      <th className="font-medium pr-3 pb-1">Angebotswert</th>
                      <th className="font-medium pr-3 pb-1">Conversion</th>
                      <th className="font-medium pr-3 pb-1 text-right">Rate</th>
                      <th className="font-medium pr-3 pb-1 text-right">angenommen / entschieden</th>
                      <th className="font-medium pr-3 pb-1 text-right">offen</th>
                      <th className="font-medium pr-3 pb-1 text-right">Angebote</th>
                      <th className="font-medium pr-3 pb-1 text-right">angeboten €</th>
                      <th className="font-medium pb-1 text-right">realisiert €</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.groessenklassen.map((k) => {
                      const ent = k.stat.angenommen + k.stat.abgelehnt + k.stat.auftragStorniert;
                      const pct = k.stat.conversion === null ? null : k.stat.conversion * 100;
                      return (
                        <tr key={k.label} className="border-t border-[#E3E3DF]">
                          <td className="pr-3 py-1 whitespace-nowrap">{k.label}</td>
                          <td className="pr-3 py-1 w-40">
                            <div className="h-4 bg-[#F3F3F1] rounded overflow-hidden min-w-[80px]">
                              <div className="h-4 rounded" style={{ width: `${pct === null ? 0 : Math.max(2, Math.round(pct))}%`, backgroundColor: '#2563EB' }} />
                            </div>
                          </td>
                          <td className="pr-3 py-1 text-right font-semibold">{pct === null ? '—' : pctFmt(pct)}</td>
                          <td className="pr-3 py-1 text-right text-[#6B6B63]">
                            {num(k.stat.angenommen)} / {num(ent)}
                          </td>
                          <td className="pr-3 py-1 text-right text-[#B45309]">{num(k.stat.offen)}</td>
                          <td className="pr-3 py-1 text-right text-[#6B6B63]">{num(k.stat.erstellt)}</td>
                          <td className="pr-3 py-1 text-right text-[#6B6B63]">{euro(k.stat.angebotenesEuro)}</td>
                          <td className="py-1 text-right text-[#15803D]">{euro(k.stat.realisiertEuro)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="mt-2 pt-2 border-t border-[#DAD9D9] text-xs text-[#6B6B63]">
                  Klassengrenzen: untere Grenze inklusiv, obere exklusiv (in <code>config.ts</code> änderbar).
                  Rate und Nenner sind identisch berechnet wie in Chart 1 und 2 — offene Angebote zählen nicht
                  in den Nenner, stehen aber als eigene Spalte, weil große Angebote länger unentschieden bleiben
                  können.
                </p>
              </div>
            </section>
          )}

          {/* Chart 5 — Zeit bis zur Annahme */}
          {stats && stats.annahmedauer.gesamt.faelle > 0 && (
            <section className="mt-6">
              <h2 className="text-lg font-semibold text-[#1A1A1A]">
                Chart 5 · Wie schnell werden Angebote angenommen?
                <span className="ml-2 text-sm font-normal text-[#6B6B63]">
                  ({num(stats.annahmedauer.gesamt.faelle)} Annahmen mit bekanntem Zeitpunkt)
                </span>
              </h2>
              <div className="mt-2 border border-[#DAD9D9] rounded p-3">
                <div className="text-sm text-[#3A3A38]">
                  Median <strong className="text-[#1A1A1A]">{tage(stats.annahmedauer.gesamt.medianTage)}</strong>
                  {' · '}Durchschnitt {tage(stats.annahmedauer.gesamt.schnittTage)}
                  {' · '}90 % der Annahmen erfolgen innerhalb von{' '}
                  <strong className="text-[#1A1A1A]">{tage(stats.annahmedauer.gesamt.p90Tage)}</strong>
                </div>

                <table className="mt-3 w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-[#9A9A90]">
                      <th className="font-medium pr-3 pb-1">Zeit bis zur Annahme</th>
                      <th className="font-medium pr-3 pb-1">Verteilung</th>
                      <th className="font-medium pr-3 pb-1 text-right">Annahmen</th>
                      <th className="font-medium pr-3 pb-1 text-right">Anteil</th>
                      <th className="font-medium pb-1 text-right">kumuliert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.annahmedauer.klassen.map((k) => (
                      <tr key={k.label} className="border-t border-[#E3E3DF]">
                        <td className="pr-3 py-1 whitespace-nowrap">{k.label}</td>
                        <td className="pr-3 py-1 w-48">
                          <div className="h-4 bg-[#F3F3F1] rounded overflow-hidden min-w-[80px]">
                            <div className="h-4 rounded" style={{ width: `${Math.round(k.anteil * 100)}%`, backgroundColor: '#15803D' }} />
                          </div>
                        </td>
                        <td className="pr-3 py-1 text-right font-semibold">{num(k.anzahl)}</td>
                        <td className="pr-3 py-1 text-right text-[#6B6B63]">{pctFmt(k.anteil * 100)}</td>
                        <td className="py-1 text-right text-[#6B6B63]">{pctFmt(k.kumuliert * 100)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Aufteilung nach Kategorien */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { titel: 'nach Angebotsgröße', zeilen: stats.annahmedauer.jeGroesse },
                    { titel: 'nach Quelle', zeilen: stats.annahmedauer.jeQuelle },
                  ].map((gruppe) => (
                    <div key={gruppe.titel}>
                      <div className="text-xs font-semibold uppercase text-[#6B6B63] mb-1">{gruppe.titel}</div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[10px] uppercase text-[#9A9A90]">
                            <th className="font-medium pr-2 pb-1"> </th>
                            <th className="font-medium pr-2 pb-1 text-right">Annahmen</th>
                            <th className="font-medium pr-2 pb-1 text-right">Median</th>
                            <th className="font-medium pb-1 text-right">90 %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gruppe.zeilen.map((z) => (
                            <tr key={z.label} className="border-t border-[#E3E3DF]">
                              <td className="pr-2 py-1 whitespace-nowrap">{z.label}</td>
                              <td className="pr-2 py-1 text-right text-[#6B6B63]">{num(z.faelle)}</td>
                              <td className="pr-2 py-1 text-right font-semibold">{tage(z.medianTage)}</td>
                              <td className="py-1 text-right text-[#6B6B63]">{tage(z.p90Tage)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>

                <p className="mt-3 pt-2 border-t border-[#DAD9D9] text-xs text-[#6B6B63]">
                  Gemessen vom Anlagedatum des Angebots bis zum Anlagedatum des ersten aktiven Auftrags —
                  der entsteht im Moment der Annahme. Grundlage sind daher nur Angebote mit auffindbarem
                  Auftrag; bei Angeboten, die nur per Status auf 3.32 stehen, ist der Zeitpunkt unbekannt.
                  Der Vergleich mit der 50-Tage-Frist zeigt, ob die Frist zum echten Entscheidungsverhalten passt.
                </p>
              </div>
            </section>
          )}

          {/* Arbeitsliste: Angebot hat Auftrag, Status sagt etwas anderes */}
          {stats && stats.statusAbweichungen.length > 0 && (
            <section className="mt-6">
              <h2 className="text-lg font-semibold text-[#1A1A1A]">
                Angebote mit Auftrag, aber abweichendem Status
                <span className="ml-2 text-sm font-normal text-[#6B6B63]">
                  ({num(stats.statusAbweichungen.length)} Stück)
                </span>
              </h2>
              <div className="mt-2 border border-[#DAD9D9] rounded p-3 bg-[#F9F9F7]">
                <p className="text-xs text-[#6B6B63]">
                  Diese Angebote werden über den Auftrag als <strong>gewonnen</strong> gezählt — der
                  Angebotsstatus in Plenty sagt etwas anderes und gehört nachgezogen. Kein Fehler im
                  Tool, sondern eine Pflege-Arbeitsliste (größte realisierte Beträge oben).
                </p>
                <table className="mt-2 w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-[#9A9A90]">
                      <th className="font-medium pr-3">Angebot</th>
                      <th className="font-medium pr-3">Status</th>
                      <th className="font-medium pr-3">Angelegt</th>
                      <th className="font-medium pr-3 text-right">Angeboten</th>
                      <th className="font-medium pr-3 text-right">Realisiert</th>
                      <th className="font-medium">Auftrag/Aufträge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.statusAbweichungen.map((a) => (
                      <tr key={a.orderId} className="border-t border-[#E3E3DF]">
                        <td className="pr-3">{a.orderId}</td>
                        <td className="pr-3">{a.statusId ?? '—'}</td>
                        <td className="pr-3">{formatDate(a.createdAt)}</td>
                        <td className="pr-3 text-right">{euro(a.invoiceTotal)}</td>
                        <td className="pr-3 text-right font-semibold text-[#15803D]">{euro(a.realisiertEuro)}</td>
                        <td>{a.auftragIds.join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Datenfehler-Hinweis: Angebote mit unerwartetem Status */}
          {stats && stats.ungeklaertListe.length > 0 && (
            <section className="mt-6 mb-10">
              <div className="p-3 rounded bg-red-50 text-red-800 text-sm">
                <div className="font-semibold">
                  ⚠ {num(stats.ungeklaertListe.length)} Angebote mit unerwartetem Status — in Plenty prüfen
                </div>
                <p className="mt-1 text-xs">
                  Diese Angebote stehen in einem Status, den es an einem Angebot nicht geben sollte
                  (z.B. <em>[3] Warten auf Zahlung</em>). Sie zählen bei „erstellt" mit, aber in keinen
                  Ausgang — solange der Status in Plenty nicht korrigiert ist, fehlen sie in der Conversion.
                  Nach der Korrektur wandern sie beim nächsten Lauf automatisch in den richtigen Ausgang.
                </p>
                <table className="mt-2 w-full max-w-xl">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-red-700/70">
                      <th className="font-medium pr-3">Angebot</th>
                      <th className="font-medium pr-3">Status</th>
                      <th className="font-medium pr-3">Angelegt</th>
                      <th className="font-medium text-right">Angeboten</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.ungeklaertListe.map((u) => (
                      <tr key={u.orderId}>
                        <td className="pr-3">{u.orderId}</td>
                        <td className="pr-3">{u.statusId ?? '—'}</td>
                        <td className="pr-3">{formatDate(u.createdAt)}</td>
                        <td className="text-right">{euro(u.invoiceTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
